/**
 * API Client for Bonndle PWA
 * Handles all API requests with offline support
 */

import { storage } from './storage';
import { getAuthToken as getBonndleAuthToken, STORAGE_KEYS } from './bonndleApi';
import { getCommentAvatar } from '../utils/avatar';
import { proxyImageUrl } from '../utils/constants';

const API_BASE = import.meta.env.VITE_API_URL || 'https://boldscaleservices.com';

// Auth token management - use the same storage key as bonndleApi for consistency
const AUTH_TOKEN_KEY = STORAGE_KEYS.ACCESS_TOKEN;

// Get auth token - prefer bonndleApi token (from login) over legacy token
export function getAuthToken() {
  // First try bonndleApi token (used by login)
  const bonndleToken = getBonndleAuthToken();
  if (bonndleToken) return bonndleToken;
  // Fallback to legacy token key for backwards compatibility
  return localStorage.getItem('bonndle_token');
}

export function setAuthToken(token) {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

/**
 * Base fetch wrapper with auth and error handling
 */
async function apiFetch(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const token = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      console.error('API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        url: url,
        errorBody: errorBody,
      });
      const errorMessage = errorBody.detail || errorBody.error || errorBody.message || errorBody.msg || JSON.stringify(errorBody) || `HTTP ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.body = errorBody;
      throw error;
    }

    return response.json();
  } catch (error) {
    // Check if offline
    if (!navigator.onLine) {
      throw new Error('OFFLINE');
    }
    throw error;
  }
}

/**
 * Queue action for offline sync
 */
async function queueOfflineAction(type, endpoint, data) {
  await storage.addPendingAction({
    type,
    endpoint,
    data,
    method: 'POST',
  });
}

/**
 * Process pending offline actions
 */
export async function syncPendingActions() {
  const pending = await storage.getPendingActions();

  for (const action of pending) {
    try {
      await apiFetch(action.endpoint, {
        method: action.method,
        body: JSON.stringify(action.data),
      });
      await storage.removePendingAction(action.id);
    } catch (error) {
      if (error.message !== 'OFFLINE') {
        // Increment retry count
        await storage.updatePendingAction(action.id, {
          retryCount: (action.retryCount || 0) + 1,
          lastError: error.message,
        });
      }
      // If offline, stop processing
      break;
    }
  }
}

// ==================== Auth API ====================

export const authApi = {
  async requestOTP(identifier, method) {
    return apiFetch('/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ identifier, method }),
    });
  },

  async verifyOTP(identifier, code) {
    const result = await apiFetch('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ identifier, code }),
    });

    if (result.token) {
      setAuthToken(result.token);
    }

    return result;
  },

  async register(userData) {
    const result = await apiFetch('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (result.token) {
      setAuthToken(result.token);
    }

    return result;
  },

  async setPin(pin) {
    return apiFetch('/auth/set-pin', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
  },

  async verifyPin(pin) {
    const result = await apiFetch('/auth/verify-pin', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });

    if (result.token) {
      setAuthToken(result.token);
    }

    return result;
  },

  logout() {
    setAuthToken(null);
    storage.clearUser();
  },
};

// ==================== User API ====================

export const userApi = {
  async getProfile() {
    try {
      const user = await apiFetch('/users/me');
      await storage.saveUser(user);
      return user;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getUser();
      }
      throw error;
    }
  },

  async updateProfile(updates) {
    try {
      const user = await apiFetch('/users/me', {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      await storage.saveUser(user);
      return user;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        // Queue for later sync
        await queueOfflineAction('updateProfile', '/users/me', updates);
        // Update local storage optimistically
        const current = await storage.getUser();
        const updated = { ...current, ...updates };
        await storage.saveUser(updated);
        return updated;
      }
      throw error;
    }
  },
};

// ==================== BonndleGroups API (Wix) ====================
// Fetches group avatars from the Wix BonndleGroups endpoint

const BONNDLE_GROUPS_URL = 'https://boldscaleservices.wixsite.com/workspaces/_functions/BonndleGroups';
const WIX_GROUP_OVERVIEW_URL = 'https://boldscaleservices.wixsite.com/workspaces/_functions/groupOverview';

export const bonndleGroupsApi = {
  /**
   * Fetch group avatars from BonndleGroups Wix endpoint
   * Returns a map of groupId -> avatar URL
   */
  async getAvatars() {
    try {
      console.log('BonndleGroups API - Fetching avatars from:', BONNDLE_GROUPS_URL);

      const response = await fetch(BONNDLE_GROUPS_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('BonndleGroups API - Response:', data);

      const groups = data.groups || [];
      const avatarMap = {};

      for (const group of groups) {
        const groupId = group.groupId;
        const avatar = group.avatar || group.image;
        if (groupId && avatar) {
          // Wrap external URLs with CORS proxy to avoid cross-origin issues
          avatarMap[groupId] = proxyImageUrl(avatar);
        }
      }

      console.log('BonndleGroups API - Extracted avatars for', Object.keys(avatarMap).length, 'groups');
      return avatarMap;
    } catch (error) {
      console.error('BonndleGroups API - Error fetching avatars:', error.message);
      return {};
    }
  },
};

// ==================== Group Overview API (Wix) ====================
// Fetches group overview content (About, How it Works, Rules) from Wix

// Default overview content (fallback when API doesn't return data)
const DEFAULT_OVERVIEW_CONTENT = {
  aboutDescription: '',
  howItWorks: [
    'Shop on merchant deals hosted in the group',
    'Get cashback rewards on verified card/bank transfer purchases',
    'Use your rewards points for future payments to save money',
  ],
  groupRules: [
    { title: 'Mutual Respect', description: 'Be respectful of other group members' },
    { title: 'Transparency', description: 'Group rewards updates are automatically shared for transparency' },
    { title: 'Security', description: 'Do not share your private info on the group thread' },
    { title: 'Enjoy', description: 'Connect with others, celebrate wins, share ideas to help everyone achieve more' },
  ],
};

export const groupOverviewApi = {
  /**
   * Get group overview content from Wix
   * @param {string} groupId - The group ID
   * @returns {Object} Overview content with aboutDescription, howItWorks, groupRules
   */
  async getOverview(groupId) {
    try {
      console.log('Group Overview API - Fetching from:', `${WIX_GROUP_OVERVIEW_URL}/${groupId}`);

      const response = await fetch(`${WIX_GROUP_OVERVIEW_URL}/${groupId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('Group Overview API - No custom overview found, using defaults');
          return DEFAULT_OVERVIEW_CONTENT;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Group Overview API - Response:', data);

      // Merge with defaults for any missing fields
      const overviewContent = data.overviewContent || {};
      return {
        aboutDescription: overviewContent.aboutDescription ?? DEFAULT_OVERVIEW_CONTENT.aboutDescription,
        howItWorks: overviewContent.howItWorks?.length > 0
          ? overviewContent.howItWorks
          : DEFAULT_OVERVIEW_CONTENT.howItWorks,
        groupRules: overviewContent.groupRules?.length > 0
          ? overviewContent.groupRules
          : DEFAULT_OVERVIEW_CONTENT.groupRules,
      };
    } catch (error) {
      console.error('Group Overview API - Error:', error.message);
      return DEFAULT_OVERVIEW_CONTENT;
    }
  },
};

// ==================== Groups API ====================
// Boldscale: /group endpoints

// Helper to filter group deals to only ACTIVE ones
const filterActiveDeals = (group) => {
  if (!group) return group;
  if (group.deals && Array.isArray(group.deals)) {
    group.deals = group.deals.filter(deal => deal.status?.toUpperCase() === 'ACTIVE');
  }
  return group;
};

export const groupsApi = {
  /**
   * Get all groups
   * Boldscale: GET /group?user_state=ALL
   * user_state options: ALL, JOINED, PENDING, etc.
   */
  async getAll(userState = 'ALL') {
    try {
      const token = getAuthToken();
      console.log('Groups API - Token available:', !!token);
      console.log('Groups API - Fetching from:', `${API_BASE}/group?user_state=${userState}`);

      const response = await apiFetch(`/group?user_state=${userState}`);
      console.log('Groups API - Response:', response);

      // API may return array directly or nested in data
      let groups = Array.isArray(response) ? response : response?.data || response?.groups || [];
      console.log('Groups API - Extracted groups:', groups.length);

      // Filter deals within each group to only show ACTIVE deals
      groups = groups.map(filterActiveDeals);

      // Try to cache, but don't fail if storage has issues
      try {
        await storage.saveGroups(groups);
        await storage.setLastSyncTime('groups');
      } catch (storageError) {
        console.warn('Groups API - Storage caching failed:', storageError.message);
      }

      return groups;
    } catch (error) {
      console.error('Groups API - Error:', error.message);
      if (error.message === 'OFFLINE') {
        return storage.getGroups();
      }
      throw error;
    }
  },

  /**
   * Get a single group by ID
   * Boldscale: GET /group/{{groupId}}
   */
  async getOne(groupId) {
    try {
      let group = await apiFetch(`/group/${groupId}`);
      // Filter deals to only show ACTIVE deals
      group = filterActiveDeals(group);
      // Try to cache, but don't fail if storage has issues
      try {
        await storage.saveGroup(group);
      } catch (storageError) {
        console.warn('Failed to cache group:', storageError.message);
      }
      return group;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getGroup(groupId);
      }
      throw error;
    }
  },

  /**
   * Get group members
   * Boldscale: GET /group/{{uuid}}/members
   */
  async getMembers(groupId) {
    try {
      const response = await apiFetch(`/group/${groupId}/members`);
      return Array.isArray(response) ? response : response?.members || response?.data || [];
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return [];
      }
      throw error;
    }
  },

  /**
   * Create a new group
   * Boldscale: POST /group
   */
  async create(groupData) {
    try {
      return apiFetch('/group', {
        method: 'POST',
        body: JSON.stringify(groupData),
      });
    } catch (error) {
      if (error.message === 'OFFLINE') {
        await queueOfflineAction('createGroup', '/group', groupData);
        return { success: true, message: 'Will create when online' };
      }
      throw error;
    }
  },

  /**
   * Join a group
   * Boldscale: POST /group/{{groupId}}/join
   */
  async join(groupId) {
    try {
      return apiFetch(`/group/${groupId}/join`, { method: 'POST' });
    } catch (error) {
      if (error.message === 'OFFLINE') {
        await queueOfflineAction('joinGroup', `/group/${groupId}/join`, {});
        return { success: true, message: 'Will join when online' };
      }
      throw error;
    }
  },

  /**
   * Invite user to group
   * Boldscale: POST /group/{{uuid}}/invite
   */
  async invite(groupId, userId) {
    try {
      return apiFetch(`/group/${groupId}/invite`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      });
    } catch (error) {
      throw error;
    }
  },

  /**
   * Accept group invite
   * Boldscale: PUT /group/invitation/{{groupId}}/accept
   */
  async acceptInvite(groupId) {
    try {
      return apiFetch(`/group/invitation/${groupId}/accept`, { method: 'PUT' });
    } catch (error) {
      throw error;
    }
  },

  /**
   * Decline group invite
   * Boldscale: PUT /group/invitation/{{groupId}}/decline
   */
  async declineInvite(groupId) {
    try {
      return apiFetch(`/group/invitation/${groupId}/decline`, { method: 'PUT' });
    } catch (error) {
      throw error;
    }
  },

  /**
   * Remove member from group
   * Boldscale: DELETE /group/{{groupId}}/delete/{{memberId}}
   */
  async removeMember(groupId, memberId) {
    try {
      return apiFetch(`/group/${groupId}/delete/${memberId}`, { method: 'DELETE' });
    } catch (error) {
      throw error;
    }
  },

  /**
   * Add deal to group
   * Boldscale: POST /group/{{groupId}}/deal
   */
  async addDeal(groupId, dealData) {
    try {
      return apiFetch(`/group/${groupId}/deal`, {
        method: 'POST',
        body: JSON.stringify(dealData),
      });
    } catch (error) {
      throw error;
    }
  },
};

// ==================== Deals API ====================

export const dealsApi = {
  async getAll(groupId = null, limit = 50) {
    try {
      const params = new URLSearchParams({ limit });
      if (groupId) params.append('group_id', groupId);

      const deals = await apiFetch(`/deals?${params}`);
      await storage.saveDeals(deals);
      await storage.setLastSyncTime('deals');
      return deals;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getDeals(groupId);
      }
      throw error;
    }
  },

  async getOne(dealId) {
    try {
      return apiFetch(`/deals/${dealId}`);
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getDeal(dealId);
      }
      throw error;
    }
  },

  async getMyDeals() {
    try {
      return apiFetch('/users/me/deals');
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getDeals();
      }
      throw error;
    }
  },

  async commit(dealId) {
    try {
      return apiFetch(`/deals/${dealId}/commit`, { method: 'POST' });
    } catch (error) {
      if (error.message === 'OFFLINE') {
        await queueOfflineAction('commitDeal', `/deals/${dealId}/commit`, {});
        return { success: true, message: 'Will commit when online' };
      }
      throw error;
    }
  },
};

// ==================== Group Code API ====================

export const groupCodeApi = {
  /**
   * Generate cashback code for a deal
   * Boldscale: POST /group-code/:groupId/deal/:marketplaceDealId
   * Works for all group types: public, private, and team groups
   */
  async generateCode(groupId, marketplaceDealId) {
    try {
      console.log('Group Code API - Generating code:', { groupId, marketplaceDealId });
      // Send empty body - some APIs require a body even for POST without data
      const response = await apiFetch(`/group-code/${groupId}/deal/${marketplaceDealId}`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      console.log('Group Code API - Response:', response);
      return response;
    } catch (error) {
      console.error('Group Code API - generateCode Error:', {
        message: error.message,
        status: error.status,
        body: error.body,
        groupId,
        marketplaceDealId,
        endpoint: `/group-code/${groupId}/deal/${marketplaceDealId}`,
      });
      throw error;
    }
  },

  /**
   * Validate a cashback code
   * Boldscale: POST /group-code/validate
   */
  async validateCode(code) {
    try {
      const response = await apiFetch('/group-code/validate', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      return response;
    } catch (error) {
      console.error('Group Code API - validateCode Error:', error.message);
      throw error;
    }
  },

  /**
   * Get user's codes
   * Boldscale: GET /group-code
   */
  async getCodes() {
    try {
      const response = await apiFetch('/group-code');
      return Array.isArray(response) ? response : response?.data || response?.codes || [];
    } catch (error) {
      console.error('Group Code API - getCodes Error:', error.message);
      throw error;
    }
  },
};

// ==================== Marketplace Deals API ====================

export const marketplaceDealsApi = {
  /**
   * Get all marketplace deals
   * Boldscale: GET /marketplace-deals
   */
  async getAll() {
    try {
      const response = await apiFetch('/marketplace-deals');
      // API may return array directly or nested in data
      const deals = Array.isArray(response) ? response : response?.data || response?.deals || [];
      // Filter to only show deals with status = ACTIVE
      return deals.filter(deal => deal.status?.toUpperCase() === 'ACTIVE');
    } catch (error) {
      console.error('Marketplace Deals API - Error:', error.message);
      throw error;
    }
  },

  /**
   * Get marketplace deal by merchant name
   * Filters deals to find one matching the merchant
   */
  async getByMerchant(merchantName) {
    try {
      const deals = await this.getAll();
      // Find deal matching merchant name (case-insensitive)
      const normalizedName = merchantName?.toLowerCase() || '';
      return deals.find(deal => {
        const dealMerchant = deal.merchant_name?.toLowerCase() || deal.marketplace?.toLowerCase() || '';
        return dealMerchant === normalizedName || dealMerchant.includes(normalizedName);
      });
    } catch (error) {
      console.error('Marketplace Deals API - getByMerchant Error:', error.message);
      return null;
    }
  },
};

// ==================== Messages API ====================

export const messagesApi = {
  async get(groupId, limit = 100, before = null) {
    try {
      const params = new URLSearchParams({ limit });
      if (before) params.append('before', before);

      const messages = await apiFetch(`/groups/${groupId}/messages?${params}`);
      await storage.saveMessages(messages);
      return messages;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getMessages(groupId);
      }
      throw error;
    }
  },

  async send(groupId, content, messageType = 'text') {
    const messageData = { content, message_type: messageType };

    try {
      const message = await apiFetch(`/groups/${groupId}/messages`, {
        method: 'POST',
        body: JSON.stringify(messageData),
      });
      await storage.addMessage(message);
      return message;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        // Create optimistic local message
        const localMessage = {
          id: `local_${Date.now()}`,
          group_id: groupId,
          content,
          message_type: messageType,
          created_at: new Date().toISOString(),
          pending: true,
        };
        await storage.addMessage(localMessage);
        await queueOfflineAction('sendMessage', `/groups/${groupId}/messages`, messageData);
        return localMessage;
      }
      throw error;
    }
  },
};

// ==================== Notifications API ====================
// Boldscale: GET /notifications, PUT /notifications/:id, DELETE /notifications/:id

export const notificationsApi = {
  /**
   * Get all notifications
   * Boldscale: GET /notifications?isRead=true (for read) or GET /notifications (for all)
   * @param {boolean} isRead - Filter by read status (optional)
   */
  async getAll(isRead = null) {
    try {
      let endpoint = '/notifications';
      if (isRead !== null) {
        endpoint += `?isRead=${isRead}`;
      }
      console.log('Notifications API - Fetching from:', endpoint);
      const response = await apiFetch(endpoint);
      console.log('Notifications API - Response:', response);
      // API may return array directly or nested in data
      const notifications = Array.isArray(response) ? response : response?.data || response?.notifications || [];
      // Try to cache, but don't fail if storage has issues
      try {
        await storage.saveNotifications(notifications);
        await storage.setLastSyncTime('notifications');
      } catch (storageError) {
        console.warn('Notifications API - Storage caching failed:', storageError.message);
      }
      return notifications;
    } catch (error) {
      console.error('Notifications API - getAll Error:', error.message);
      if (error.message === 'OFFLINE') {
        try {
          return await storage.getNotifications();
        } catch {
          return [];
        }
      }
      throw error;
    }
  },

  /**
   * Mark notification as read
   * Boldscale: PUT /notifications/{{notificationId}}
   */
  async markRead(notificationId) {
    try {
      // Send all possible field names to ensure compatibility (API uses hasRead)
      const response = await apiFetch(`/notifications/${notificationId}`, {
        method: 'PUT',
        body: JSON.stringify({ hasRead: true, is_read: true, isRead: true, read: true }),
      });
      console.log('Notifications API - markRead response:', response);
      try {
        await storage.markNotificationRead(notificationId);
      } catch (storageError) {
        console.warn('Notifications API - Storage update failed:', storageError.message);
      }
      return response;
    } catch (error) {
      console.error('Notifications API - markRead Error:', error.message);
      if (error.message === 'OFFLINE') {
        try {
          await storage.markNotificationRead(notificationId);
          await queueOfflineAction('markRead', `/notifications/${notificationId}`, { is_read: true, isRead: true, read: true });
        } catch {
          // Ignore storage errors
        }
      }
      throw error;
    }
  },

  /**
   * Delete notification
   * Boldscale: DELETE /notifications/{{notificationId}}
   */
  async deleteNotification(notificationId) {
    try {
      const response = await apiFetch(`/notifications/${notificationId}`, { method: 'DELETE' });
      return response;
    } catch (error) {
      console.error('Notifications API - deleteNotification Error:', error.message);
      throw error;
    }
  },

  /**
   * Mark all notifications as read (client-side loop)
   * Note: Boldscale doesn't have a bulk mark-read endpoint, so we mark each individually
   */
  async markAllRead(notificationIds) {
    try {
      const results = await Promise.all(
        notificationIds.map(id => this.markRead(id).catch(err => ({ error: err, id })))
      );
      return { success: true, results };
    } catch (error) {
      console.error('Notifications API - markAllRead Error:', error.message);
      if (error.message === 'OFFLINE') {
        try {
          await queueOfflineAction('markAllRead', '/notifications/mark-all-read', { notificationIds });
        } catch {
          // Ignore storage errors
        }
        return { success: true };
      }
      throw error;
    }
  },
};

// ==================== Rewards API ====================

export const rewardsApi = {
  async getSummary() {
    try {
      const rewards = await apiFetch('/rewards');
      await storage.saveRewards(rewards);
      return rewards;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getRewards();
      }
      throw error;
    }
  },

  async getHistory(limit = 50) {
    return apiFetch(`/rewards/history?limit=${limit}`);
  },

  async redeem(amount, description = 'Reward redemption') {
    return apiFetch('/rewards/redeem', {
      method: 'POST',
      body: JSON.stringify({ amount, description }),
    });
  },
};

// ==================== Payment Methods API ====================

export const paymentApi = {
  async getAll() {
    return apiFetch('/payment-methods');
  },

  async add(paymentData) {
    return apiFetch('/payment-methods', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },

  async remove(paymentId) {
    return apiFetch(`/payment-methods/${paymentId}`, { method: 'DELETE' });
  },

  async setDefault(paymentId) {
    return apiFetch(`/payment-methods/${paymentId}/default`, { method: 'POST' });
  },

  /**
   * Initialize a payment transaction
   * Boldscale: POST /payments/initialized-payment
   * @param {Object} paymentData - Payment details
   * @param {number} paymentData.amount - Amount to pay
   * @param {string} paymentData.paymentReference - Unique payment reference (generated client-side)
   * @param {string} paymentData.currencyCode - Currency code (e.g., "NGN")
   * @param {string} paymentData.channel - Payment channel: "card" or "transfer"
   */
  async initializePayment(paymentData) {
    try {
      console.log('Payment API - Initializing payment:', paymentData);
      const response = await apiFetch('/payments/initialized-payment', {
        method: 'POST',
        body: JSON.stringify(paymentData),
      });
      console.log('Payment API - Initialize response:', response);
      return response;
    } catch (error) {
      console.error('Payment API - initializePayment Error:', {
        message: error.message,
        status: error.status,
        body: error.body,
        paymentData,
      });
      throw error;
    }
  },
};

// ==================== Providers API ====================

// Helper to extract array from API response (handles both array and paginated responses)
const extractArray = (response) => {
  if (!response) return [];
  // If response is already an array, return it
  if (Array.isArray(response)) return response;
  // If response has a data property that's an array, return that
  if (response.data && Array.isArray(response.data)) return response.data;
  // If response has items property
  if (response.items && Array.isArray(response.items)) return response.items;
  // If response has results property
  if (response.results && Array.isArray(response.results)) return response.results;
  // If response has providers property
  if (response.providers && Array.isArray(response.providers)) return response.providers;
  // Return empty array as fallback
  console.warn('Unexpected API response format:', response);
  return [];
};

export const providersApi = {
  async getAll(vtuProvider = 'Internal', limit = 1000) {
    try {
      // Fetch providers with pagination - try to get all with high limit
      const response = await apiFetch(`/providers?vtuProvider=${vtuProvider}&page=1&limit=${limit}`);
      const providers = extractArray(response);
      console.log(`Fetched ${providers.length} providers from API (vtuProvider=${vtuProvider})`, response);
      return providers;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        // Return cached or mock data for offline mode
        return storage.getProviders() || [];
      }
      throw error;
    }
  },

  async getAllProviders(limit = 1000) {
    try {
      // Fetch ALL providers with high limit to ensure we get everything
      const response = await apiFetch(`/providers?page=1&limit=${limit}`);
      const providers = extractArray(response);
      console.log(`Fetched ${providers.length} total providers from API (no filter)`, response);
      return providers;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getProviders() || [];
      }
      throw error;
    }
  },

  async getOne(providerId) {
    try {
      return apiFetch(`/providers/${providerId}`);
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getProvider(providerId);
      }
      throw error;
    }
  },

  async getByService(serviceId, limit = 500) {
    try {
      // Fetch providers by service with pagination
      const response = await apiFetch(`/providers?serviceId=${serviceId}&page=1&limit=${limit}`);
      const providers = extractArray(response);
      console.log(`Fetched ${providers.length} providers for service ${serviceId}`);
      return providers;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        const allProviders = storage.getProviders() || [];
        return allProviders.filter(p => p.service_uuid === serviceId);
      }
      throw error;
    }
  },
};

// ==================== Services API ====================

export const servicesApi = {
  async getAll(limit = 100) {
    try {
      // Fetch all services with pagination
      const response = await apiFetch(`/services?page=1&limit=${limit}`);
      const services = extractArray(response);
      console.log(`Fetched ${services.length} services from API`, response);
      return services;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getServices() || [];
      }
      throw error;
    }
  },

  async getOne(serviceId) {
    try {
      return apiFetch(`/services/${serviceId}`);
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getService(serviceId);
      }
      throw error;
    }
  },
};

// ==================== Products API ====================

export const productsApi = {
  async getAll(vtuProvider = 'Internal') {
    try {
      const products = await apiFetch(`/products?vtuProvider=${vtuProvider}`);
      return products;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getProducts() || [];
      }
      throw error;
    }
  },

  async getOne(productId) {
    try {
      return apiFetch(`/products/${productId}`);
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getProduct(productId);
      }
      throw error;
    }
  },

  async getByService(serviceId, providerId = null) {
    try {
      let endpoint = `/products?serviceId=${serviceId}`;
      if (providerId) endpoint += `&providerId=${providerId}`;
      return apiFetch(endpoint);
    } catch (error) {
      if (error.message === 'OFFLINE') {
        const products = storage.getProducts() || [];
        return products.filter(p =>
          p.serviceId === serviceId &&
          (!providerId || p.providerId === providerId)
        );
      }
      throw error;
    }
  },

  /**
   * Get products by category
   * Available categories: mobile_top_up, mobile_pin, bill_payment, mobile_data,
   * voice_data_combo, internet_services, cable_tv, utilities, utilities_electricity,
   * utilities_water, utilities_transport_toll, betting, taxes, taxes_federal,
   * taxes_state, lifestyle, lifestyle_education, lifestyle_healthcare,
   * lifestyle_insurance, retail_gift_card, e_vouchers
   */
  async getByCategory(category, providerId = null) {
    try {
      let endpoint = `/products?productCategory=${category}`;
      if (providerId) endpoint += `&providerId=${providerId}`;
      const response = await apiFetch(endpoint);
      // API returns { popularPlans: [], all: [] } format
      if (response && response.all) {
        return response.all;
      }
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      if (error.message === 'OFFLINE') {
        const products = storage.getProducts() || [];
        return products.filter(p =>
          p.category === category &&
          (!providerId || p.providerId === providerId)
        );
      }
      throw error;
    }
  },

  /**
   * Get all products for a specific provider
   * Returns products grouped by category_name
   */
  async getByProvider(providerId) {
    try {
      const response = await apiFetch(`/products?providerId=${providerId}`);
      // API returns { popularPlans: [], all: [] } format
      if (response && response.all) {
        return response.all;
      }
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      if (error.message === 'OFFLINE') {
        const products = storage.getProducts() || [];
        return products.filter(p => p.provider_uuid === providerId);
      }
      throw error;
    }
  },

  async lookup(productId, accountId) {
    return apiFetch(`/products/${productId}/lookup/${accountId}`);
  },

  /**
   * Purchase product with Bonndle Rewards (points)
   * Boldscale: POST /products/purchase-with-points
   * @param {Object} purchaseData - Purchase details
   * @param {string} purchaseData.account - Recipient account (phone number, meter number, etc.)
   * @param {string} purchaseData.productId - Product UUID
   * @param {number} purchaseData.purchaseAmount - Amount to pay (integer)
   * @param {number} purchaseData.donationAmount - Donation amount (integer, usually 0)
   * @param {number} purchaseData.totalAmount - Total amount (integer)
   * @param {number} purchaseData.totalAmountInUSD - Total amount in USD
   * @param {number} purchaseData.quantity - Quantity (integer, usually 1)
   * @param {string} purchaseData.currencyCode - Currency code (e.g., "NGN")
   */
  async purchaseWithPoints(purchaseData) {
    try {
      console.log('Products API - Purchase with points:', purchaseData);
      const response = await apiFetch('/products/purchase-with-points', {
        method: 'POST',
        body: JSON.stringify(purchaseData),
      });
      console.log('Products API - Purchase with points response:', response);
      return response;
    } catch (error) {
      console.error('Products API - purchaseWithPoints Error:', {
        message: error.message,
        status: error.status,
        body: error.body,
        purchaseData,
      });
      throw error;
    }
  },

  /**
   * Purchase product with card
   * Boldscale: POST /products/purchase-with-card
   * @param {Object} purchaseData - Purchase details
   * @param {string} purchaseData.account - Recipient account (phone number, meter number, etc.)
   * @param {string} purchaseData.productId - Product UUID
   * @param {number} purchaseData.purchaseAmount - Amount to pay (integer)
   * @param {number} purchaseData.donationAmount - Donation amount (integer, usually 0)
   * @param {number} purchaseData.totalAmount - Total amount (integer)
   * @param {number} purchaseData.totalAmountInUSD - Total amount in USD
   * @param {number} purchaseData.quantity - Quantity (integer, usually 1)
   * @param {string} purchaseData.currencyCode - Currency code (e.g., "NGN")
   * @param {string} purchaseData.cardId - Saved card UUID
   */
  async purchaseWithCard(purchaseData) {
    try {
      console.log('Products API - Purchase with card:', purchaseData);
      const response = await apiFetch('/products/purchase-with-card', {
        method: 'POST',
        body: JSON.stringify(purchaseData),
      });
      console.log('Products API - Purchase with card response:', response);
      return response;
    } catch (error) {
      console.error('Products API - purchaseWithCard Error:', {
        message: error.message,
        status: error.status,
        body: error.body,
        purchaseData,
      });
      throw error;
    }
  },

  /**
   * Purchase product with bank transfer
   * Boldscale: POST /products/purchase-with-transfer
   * @param {Object} purchaseData - Purchase details
   * @param {string} purchaseData.reference - Unique transaction reference
   * @param {string} purchaseData.account - Recipient account (phone number, meter number, etc.)
   * @param {string} purchaseData.productId - Product UUID
   * @param {number} purchaseData.purchaseAmount - Amount to pay (integer)
   * @param {number} purchaseData.donationAmount - Donation amount (integer, usually 0)
   * @param {number} purchaseData.totalAmount - Total amount (integer)
   * @param {number} purchaseData.quantity - Quantity (integer, usually 1)
   * @param {string} purchaseData.currencyCode - Currency code (e.g., "NGN")
   * @param {string} [purchaseData.discountCode] - Optional discount/promo code
   * @returns {Promise<{data: {paymentUrl: string, reference: string}}>} - Payment URL to complete transfer
   */
  async purchaseWithTransfer(purchaseData) {
    try {
      console.log('Products API - Purchase with transfer:', purchaseData);
      const response = await apiFetch('/products/purchase-with-transfer', {
        method: 'POST',
        body: JSON.stringify(purchaseData),
      });
      console.log('Products API - Purchase with transfer response:', response);
      return response;
    } catch (error) {
      console.error('Products API - purchaseWithTransfer Error:', {
        message: error.message,
        status: error.status,
        body: error.body,
        purchaseData,
      });
      throw error;
    }
  },

  /**
   * Get gift cards with pagination
   * Boldscale: GET /products/gift-cards?page=1&limit=100
   */
  async getGiftCards(page = 1, limit = 100) {
    try {
      const response = await apiFetch(`/products/gift-cards?page=${page}&limit=${limit}`);
      // API may return array directly or nested in data/all
      if (response && response.all) {
        return response.all;
      }
      if (response && response.data) {
        return response.data;
      }
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      if (error.message === 'OFFLINE') {
        const products = storage.getProducts() || [];
        return products.filter(p => {
          const category = p.category_name?.toLowerCase() || p.category?.toLowerCase() || '';
          return category.includes('gift') || category.includes('voucher') || category.includes('e_voucher');
        });
      }
      throw error;
    }
  },
};

// ==================== Feed API ====================

export const feedApi = {
  async get(limit = 20, offset = 0) {
    try {
      const feed = await apiFetch(`/feed?limit=${limit}&offset=${offset}`);
      await storage.saveFeed(feed);
      await storage.setLastSyncTime('feed');
      return feed;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        return storage.getFeed();
      }
      throw error;
    }
  },

  async toggleLike(feedId) {
    try {
      const item = await apiFetch(`/feed/${feedId}/like`, { method: 'POST' });
      await storage.updateFeedItem(item);
      return item;
    } catch (error) {
      if (error.message === 'OFFLINE') {
        await queueOfflineAction('toggleLike', `/feed/${feedId}/like`, {});
        // Optimistic update
        const feed = await storage.getFeed();
        const item = feed.find((f) => f.id === feedId);
        if (item) {
          item.is_liked = !item.is_liked;
          item.likes = item.is_liked ? item.likes + 1 : item.likes - 1;
          await storage.updateFeedItem(item);
          return item;
        }
      }
      throw error;
    }
  },
};

// ==================== Wix Feed API ====================

const WIX_FEED_URL = import.meta.env.VITE_WIX_FEED_URL;
const WIX_API_KEY = import.meta.env.VITE_WIX_API_KEY;

export const wixFeedApi = {
  /**
   * Get home feed data from Wix
   * Endpoint: https://boldscaleservices.wixsite.com/workspaces/_functions/bonndleFeed
   * Requires x-api-key header
   */
  async getFeed() {
    try {
      console.log('Wix Feed API - Fetching from:', WIX_FEED_URL);

      const response = await fetch(WIX_FEED_URL, {
        method: 'GET',
        headers: {
          'x-api-key': WIX_API_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        console.error('Wix Feed API - Error Response:', {
          status: response.status,
          statusText: response.statusText,
          errorBody,
        });
        throw new Error(errorBody.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Wix Feed API - Response:', data);

      // Handle different response formats
      const feedItems = Array.isArray(data) ? data : data?.items || data?.feed || data?.data || [];

      // Map Wix fields to app fields (camelCase) if needed
      const normalizedFeed = feedItems
        .map((item) => ({
          id: item._id || item.id,
          type: item.type || 'story',
          author: item.author || item.brand, // brand is used for sponsored content
          avatar: (item.author || item.brand || '').toLowerCase().includes('bonndle')
            ? '/logo/bonndleLogoIcon_Indigo.svg'
            : item.avatar,
          title: item.title,
          content: item.content,
          image: item.image || null,
          dealBadge: item.dealBadge || item.deal_badge || null,
          groupId: item.groupId !== 'null' ? item.groupId : null,
          groupName: item.group !== 'null' ? item.group : null,
          dealId: item.dealId !== 'null' ? item.dealId : null,
          likes: item.likes || 0,
          comments: item.comments || 0,
          isLiked: item.isLiked || item.is_liked || false,
          time: item.time || item.date || 'Just now',
          date: item.date || null,
          status: item.status || 'published',
          thread: item.thread || [], // Array of { uuid, comment, date }
          createdAt: item.createdAt || item.created_at || item._createdDate || new Date().toISOString(),
          updatedAt: item._updatedDate || null,
        }))
        // Only show published items
        .filter((item) => item.status === 'published');

      // Cache the feed
      try {
        await storage.saveFeed(normalizedFeed);
        await storage.setLastSyncTime('wixFeed');
      } catch (storageError) {
        console.warn('Wix Feed API - Storage caching failed:', storageError.message);
      }

      return normalizedFeed;
    } catch (error) {
      console.error('Wix Feed API - Error:', error.message);

      // Check if offline
      if (!navigator.onLine) {
        try {
          return await storage.getFeed();
        } catch {
          return [];
        }
      }

      throw error;
    }
  },
};

// ==================== Wix Comments API ====================

const WIX_COMMENTS_URL = import.meta.env.VITE_WIX_COMMENTS_URL || WIX_FEED_URL?.replace('/bonndleFeed', '/bonndleComments');

export const wixCommentsApi = {
  /**
   * Get comments for a feed item
   * @param {string} feedItemId - The feed item ID
   * @param {number} limit - Max comments to fetch
   * @param {number} offset - Pagination offset
   */
  async getComments(feedItemId, limit = 50, offset = 0) {
    try {
      const url = `${WIX_COMMENTS_URL}?feedItemId=${feedItemId}&limit=${limit}&offset=${offset}`;
      console.log('Wix Comments API - Fetching from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'x-api-key': WIX_API_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        console.error('Wix Comments API - Error Response:', {
          status: response.status,
          errorBody,
        });
        throw new Error(errorBody.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Wix Comments API - Response:', data);

      // Normalize comments
      const comments = (data.comments || []).map(comment => ({
        id: comment.id || comment._id,
        feedItemId: comment.feedItemId,
        author: comment.author,
        avatar: comment.avatar || getCommentAvatar(null, comment.author),
        text: comment.text,
        userId: comment.userId,
        time: comment.time || formatTimeAgo(comment.timestamp),
        timestamp: comment.timestamp,
        date: comment.date,
      }));

      return {
        comments,
        totalCount: data.totalCount || comments.length,
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Wix Comments API - getComments Error:', error.message);
      // Return empty on error (fallback to local state)
      return { comments: [], totalCount: 0, hasMore: false };
    }
  },

  /**
   * Add a comment to a feed item
   * @param {string} feedItemId - The feed item ID
   * @param {Object} commentData - Comment data { author, avatar, text, userId }
   */
  async addComment(feedItemId, commentData) {
    try {
      console.log('Wix Comments API - Adding comment:', { feedItemId, commentData });

      const response = await fetch(WIX_COMMENTS_URL, {
        method: 'POST',
        headers: {
          'x-api-key': WIX_API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedItemId,
          author: commentData.author,
          avatar: commentData.avatar || getCommentAvatar(null, commentData.author),
          text: commentData.text,
          userId: commentData.userId || null,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        console.error('Wix Comments API - Error Response:', {
          status: response.status,
          errorBody,
        });
        throw new Error(errorBody.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Wix Comments API - Add comment response:', data);

      // Return the created comment
      return {
        success: true,
        comment: {
          id: data.comment?.id || data.comment?._id || Date.now().toString(),
          feedItemId,
          author: commentData.author,
          avatar: commentData.avatar || getCommentAvatar(null, commentData.author),
          text: commentData.text,
          userId: commentData.userId,
          time: 'Just now',
          timestamp: Date.now(),
          date: new Date().toISOString(),
        },
      };
    } catch (error) {
      console.error('Wix Comments API - addComment Error:', error.message);
      throw error;
    }
  },

  /**
   * Delete a comment
   * @param {string} commentId - The comment ID to delete
   */
  async deleteComment(commentId) {
    try {
      const url = `${WIX_COMMENTS_URL}?commentId=${commentId}`;
      console.log('Wix Comments API - Deleting comment:', commentId);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'x-api-key': WIX_API_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || `HTTP ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Wix Comments API - deleteComment Error:', error.message);
      throw error;
    }
  },
};

// ==================== Wix FAQ API ====================

const WIX_FAQ_URL = import.meta.env.VITE_WIX_FAQ_URL || WIX_FEED_URL?.replace('/bonndleFeed', '/bonndleFAQ');

export const wixFAQApi = {
  /**
   * Get FAQ items from Wix
   * @param {string} category - Optional category filter
   */
  async getFAQ(category = null) {
    try {
      const url = new URL(WIX_FAQ_URL);
      if (category) {
        url.searchParams.set('category', category);
      }

      console.log('Wix FAQ API - Fetching from:', url.toString());

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'x-api-key': WIX_API_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        console.error('Wix FAQ API - Error Response:', {
          status: response.status,
          errorBody,
        });
        throw new Error(errorBody.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Wix FAQ API - Response:', data);

      // Normalize FAQ items to match app format { q, a }
      const faqItems = (data.items || []).map(item => ({
        id: item._id || item.id,
        q: item.question,
        a: item.answer,
        category: item.category || 'General',
        order: item.order || 0,
      }));

      return faqItems;
    } catch (error) {
      console.error('Wix FAQ API - getFAQ Error:', error.message);
      // Return empty array on error (component will fallback to static data)
      return [];
    }
  },
};

// ==================== Wix Likes API ====================

const WIX_LIKES_URL = import.meta.env.VITE_WIX_LIKES_URL || WIX_FEED_URL?.replace('/bonndleFeed', '/bonndleLikes');

export const wixLikesApi = {
  /**
   * Toggle like for a feed item
   * @param {string} feedItemId - The feed item ID
   * @param {string} userId - Optional user ID for tracking
   */
  async toggleLike(feedItemId, userId = null) {
    try {
      console.log('Wix Likes API - Toggling like:', { feedItemId, userId });

      const response = await fetch(WIX_LIKES_URL, {
        method: 'POST',
        headers: {
          'x-api-key': WIX_API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedItemId,
          userId,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        console.error('Wix Likes API - Error Response:', {
          status: response.status,
          errorBody,
        });
        throw new Error(errorBody.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Wix Likes API - Response:', data);

      return {
        success: true,
        feedItemId,
        likes: data.likes,
        isLiked: data.isLiked,
      };
    } catch (error) {
      console.error('Wix Likes API - toggleLike Error:', error.message);
      throw error;
    }
  },

  /**
   * Get like status for a feed item
   * @param {string} feedItemId - The feed item ID
   * @param {string} userId - Optional user ID to check if liked
   */
  async getLikeStatus(feedItemId, userId = null) {
    try {
      let url = `${WIX_LIKES_URL}?feedItemId=${feedItemId}`;
      if (userId) {
        url += `&userId=${userId}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'x-api-key': WIX_API_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return {
        feedItemId,
        likes: data.likes || 0,
        isLiked: data.isLiked || false,
      };
    } catch (error) {
      console.error('Wix Likes API - getLikeStatus Error:', error.message);
      return { feedItemId, likes: 0, isLiked: false };
    }
  },
};

// Helper: Format timestamp to "X ago" format
function formatTimeAgo(timestamp) {
  if (!timestamp) return '';
  const now = Date.now();
  const diff = now - timestamp;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ==================== Health Check ====================

export async function checkHealth() {
  try {
    const result = await fetch(`${API_BASE}/health`);
    return result.ok;
  } catch {
    return false;
  }
}

// Auto-sync when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('Back online, syncing pending actions...');
    syncPendingActions();
  });
}

// ==================== Transactions API ====================

export const transactionsApi = {
  /**
   * Get recent transactions (all types)
   * Boldscale: GET /transactions (includes all transaction types)
   */
  async getRecent() {
    try {
      const response = await apiFetch('/transactions');
      console.log('Transactions API - Recent transactions:', response);
      // API may return array directly or nested in data
      const transactions = Array.isArray(response) ? response : response?.data || response?.transactions || [];
      // Return only the most recent transactions (already sorted by created_at desc from API)
      return transactions.slice(0, 20);
    } catch (error) {
      console.error('Transactions API - getRecent Error:', {
        message: error.message,
        status: error.status,
        body: error.body,
      });
      throw error;
    }
  },

  /**
   * Get recent purchase transactions (filtered)
   * Boldscale: GET /transactions/recent-transactions
   * Returns recent product purchases for quick repeat transactions
   */
  async getRecentTransactions() {
    try {
      const response = await apiFetch('/transactions/recent-transactions');
      console.log('Transactions API - Recent purchase transactions:', response);
      // API may return array directly or nested in data
      const transactions = Array.isArray(response) ? response : response?.data || response?.transactions || [];
      return transactions;
    } catch (error) {
      console.error('Transactions API - getRecentTransactions Error:', {
        message: error.message,
        status: error.status,
        body: error.body,
      });
      throw error;
    }
  },

  /**
   * Get points earned today
   * Boldscale: GET /transactions/points-reward-earned-today
   */
  async getPointsEarnedToday() {
    try {
      const response = await apiFetch('/transactions/points-reward-earned-today');
      console.log('Transactions API - Points earned today:', response);
      return response;
    } catch (error) {
      console.error('Transactions API - getPointsEarnedToday Error:', {
        message: error.message,
        status: error.status,
        body: error.body,
      });
      throw error;
    }
  },
};

// ==================== Defaults API ====================

export const defaultsApi = {
  /**
   * Get system defaults including exchange rates
   * Boldscale: GET /defaults
   * Returns: { oneUSDtoNGN, boldCreditPerNaira, nairaPerBoldCredit, ... }
   */
  async get() {
    try {
      const response = await apiFetch('/defaults');
      console.log('Defaults API - Response:', response);
      return response;
    } catch (error) {
      console.error('Defaults API - get Error:', {
        message: error.message,
        status: error.status,
        body: error.body,
      });
      throw error;
    }
  },
};

export default {
  auth: authApi,
  user: userApi,
  groups: groupsApi,
  bonndleGroups: bonndleGroupsApi,
  groupOverview: groupOverviewApi,
  deals: dealsApi,
  groupCode: groupCodeApi,
  marketplaceDeals: marketplaceDealsApi,
  messages: messagesApi,
  notifications: notificationsApi,
  rewards: rewardsApi,
  payment: paymentApi,
  feed: feedApi,
  wixFeed: wixFeedApi,
  wixComments: wixCommentsApi,
  wixLikes: wixLikesApi,
  wixFAQ: wixFAQApi,
  providers: providersApi,
  services: servicesApi,
  products: productsApi,
  transactions: transactionsApi,
  defaults: defaultsApi,
  checkHealth,
  syncPendingActions,
  setAuthToken,
  getAuthToken,
};
